import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  FileDown,
  Share2,
  CheckCircle,
  X,
  Copy,
  Loader2,
  FileText,
  AlertTriangle,
  Eye,
  Info,
  Link as LinkIcon,
  ArrowRight,
  Shield,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  useReportDashboard,
  useGenerateShareLink,
  useRevokeShareLink,
  useGenerateTextReport,
  useCurrentAssessmentId,
} from "@/hooks/use-report-dashboard";
// @deprecated - Old client-side PDF generation (kept for reference)
// import { generatePdfReport } from "@/service/pdf-generator";
import ReportVisualization from "@/components/reports/ReportVisualization";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import { formatSectorLabel } from "@/lib/sector-labels";
import { useQuery } from "@tanstack/react-query";

export default function Report() {
  const [match, params] = useRoute("/report/:assessmentId");
  const urlAssessmentId = params?.assessmentId;
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { i18n } = useTranslation();

  const { data: aiQuotaData } = useQuery({
    queryKey: ["workspace-ai-quota"],
    queryFn: async () => {
      const response = await fetch("/api/workspace/ai-quota", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch AI quota");
      return response.json();
    },
    enabled: !!user,
  });
  const hasAiBudget = aiQuotaData?.quota != null && aiQuotaData.usedCents < aiQuotaData.quota.budgetCents;

  const [showShareModal, setShowShareModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Block platform admins from accessing /report without ID
  useEffect(() => {
    if (user && user.role === "admin" && !urlAssessmentId) {
      toast({
        title: "Access Denied",
        description: "Platform administrators must view reports through /admin/reports. Redirecting...",
        variant: "destructive",
      });
      setTimeout(() => {
        setLocation("/admin/reports");
      }, 2000);
    }
  }, [user, urlAssessmentId, setLocation, toast]);

  // Fetch current assessment if no URL param provided (company users only)
  const {
    data: currentAssessmentData,
    isLoading: isLoadingCurrentAssessment,
    error: currentAssessmentError
  } = useCurrentAssessmentId();

  // Determine which assessment ID to use
  const assessmentId = urlAssessmentId || currentAssessmentData?.assessmentId;

  // Fetch dashboard data
  const { data: dashboardData, isLoading, error } = useReportDashboard(assessmentId);

  // Handle authorization errors
  useEffect(() => {
    if (error && error.message?.includes("permission")) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to view this assessment. Redirecting...",
        variant: "destructive",
      });

      // Redirect to their own report page after 2 seconds
      setTimeout(() => {
        setLocation("/report");
      }, 2000);
    }
  }, [error, setLocation, toast]);

  // Mutations
  const generateShareLink = useGenerateShareLink();
  const revokeShareLink = useRevokeShareLink();
  const generateTextReport = useGenerateTextReport();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingMeasuresPdf, setIsGeneratingMeasuresPdf] = useState(false);

  const handleCopyShareLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied!",
      description: "Share link copied to clipboard",
    });
  };

  const handleGenerateShareLink = async () => {
    if (!assessmentId) return;

    try {
      const result = await generateShareLink.mutateAsync({
        assessmentId,
        expiresInDays: 30
      });

      toast({
        title: "Share link generated!",
        description: "Your report is now accessible via the share link",
      });

      handleCopyShareLink(result.shareUrl);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate share link",
        variant: "destructive",
      });
    }
  };

  const handleRevokeShareLink = async () => {
    if (!dashboardData?.shareLink || !assessmentId) return;

    try {
      await revokeShareLink.mutateAsync({
        token: dashboardData.shareLink.token,
        assessmentId,
      });

      toast({
        title: "Share link revoked",
        description: "The share link is no longer accessible",
      });

      setShowShareModal(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke share link",
        variant: "destructive",
      });
    }
  };

  const handleGenerateTextReport = async () => {
    if (!assessmentId || !dashboardData?.assessment) {
      toast({
        title: "Error",
        description: "No assessment found",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await generateTextReport.mutateAsync({ assessmentId, locale: i18n.language });

      // Navigate to TextReport page with the report data
      setLocation("/report/text", {
        state: {
          report: {
            report: result.report,
            assessmentId,
            organizationName: dashboardData.assessment.organizationName,
            reportingYear: dashboardData.carbonPerformance.reportingPeriod,
          }
        }
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate text report",
        variant: "destructive",
      });
    }
  };

  const handleGeneratePdf = async () => {
    if (!dashboardData || !assessmentId) {
      toast({
        title: "Error",
        description: "Report data not available",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      toast({
        title: "Generating PDF",
        description: "Please wait while your report is being generated...",
      });

      const response = await fetch(`/api/reports/${assessmentId}/pdf`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers
          .get("Content-Disposition")
          ?.match(/filename="(.+)"/)?.[1] || `report_${assessmentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: "Your sustainability report PDF has been downloaded",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate PDF",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleGenerateMeasuresPdf = async () => {
    if (!dashboardData || !assessmentId) {
      toast({
        title: "Error",
        description: "Report data not available",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingMeasuresPdf(true);
      toast({
        title: "Generating PDF",
        description: "Please wait while your measures report is being generated...",
      });

      const response = await fetch(`/api/reports/${assessmentId}/measures-pdf`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to generate measures PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers
          .get("Content-Disposition")
          ?.match(/filename="(.+)"/)?.[1] || `measures_report_${assessmentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: "Your measures & mitigation PDF has been downloaded",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate measures PDF",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMeasuresPdf(false);
    }
  };

  // Loading state
  if (isLoading || isLoadingCurrentAssessment) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <Skeleton className="h-9 w-64 mb-2" />
            <Skeleton className="h-5 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  // No finalized assessment available
  if (!urlAssessmentId && currentAssessmentError) {
    return (
      <div>
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-black text-slate-900">{"Sustainability Report"}</h1>
          <p className="text-base text-slate-500">{"No finalized report available"}</p>
        </div>

        <div className="pt-12 flex flex-col gap-6">
          {/* Main Card */}
          <div className="p-10 bg-white rounded-2xl border-2 border-slate-500/20 flex flex-col items-center gap-6">
            {/* Icon */}
            <div className="w-16 h-16 bg-slate-500/10 rounded-full flex items-center justify-center">
              <FileText className="h-7 w-7 text-primary" />
            </div>

            {/* Title & Description */}
            <div className="max-w-lg flex flex-col items-center gap-2 text-center">
              <h2 className="text-xl font-bold text-primary">{"No Finalized Assessment"}</h2>
              <p className="text-base text-slate-500 leading-6">{"You don't have any finalized assessments yet. Create and complete an assessment to view your sustainability report."}</p>
            </div>

            {/* Steps */}
            <div className="w-full max-w-[672px] pt-4 flex justify-center gap-6">
              {/* Step 1 */}
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-10 h-10 bg-white rounded-full border-2 border-primary flex items-center justify-center">
                  <span className="text-base font-bold text-primary">1</span>
                </div>
                <span className="text-sm font-bold text-slate-900 text-center">{"Create an assessment"}</span>
              </div>
              {/* Step 2 */}
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-10 h-10 bg-white rounded-full border-2 border-slate-500/30 flex items-center justify-center">
                  <span className="text-base font-bold text-slate-400">2</span>
                </div>
                <span className="text-sm font-bold text-slate-400 text-center">{"Complete data collection"}</span>
              </div>
              {/* Step 3 */}
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-10 h-10 bg-white rounded-full border-2 border-slate-500/30 flex items-center justify-center">
                  <span className="text-base font-bold text-slate-400">3</span>
                </div>
                <span className="text-sm font-bold text-slate-400 text-center">{"Finalize and lock"}</span>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-6">
              <Button
                size="lg"
                className="rounded-3xl px-8 py-3 text-base font-bold bg-primary hover:bg-primary/90"
                onClick={() => setLocation("/assessments")}
              >
                {"Go to Assessments"}
              </Button>
            </div>
          </div>

          {/* Getting Started Card */}
          <div className="p-10 bg-slate-500/5 rounded-2xl border-2 border-slate-500/20 flex flex-col items-center gap-6">
            {/* Icon */}
            <div className="w-16 h-16 bg-slate-500/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-primary" />
            </div>

            {/* Title & Description */}
            <div className="max-w-[672px] flex flex-col items-center gap-2 text-center">
              <h2 className="text-xl font-bold text-primary">{"Getting Started"}</h2>
              <p className="text-base text-slate-500 leading-6">{"Navigate to the Assessments page to create your first assessment. Once completed and finalized, you'll be able to generate and share your sustainability report here."}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state (including authorization errors and locked status)
  if (error || !dashboardData) {
    // Check if error is because assessment is not locked
    const isNotLockedError = error?.message?.includes("must be locked");

    if (isNotLockedError) {
      return (
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">{"Sustainability Report"}</h1>
            <p className="text-muted-foreground mt-1">{"Complete and lock your assessment to view the report"}</p>
          </div>

          <Card className="border-2 border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl text-amber-800">
                <Shield className="h-6 w-6" />
                {"Assessment Not Locked"}
              </CardTitle>
              <CardDescription className="text-amber-700">
                {"Your assessment must be locked before you can view the sustainability report."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white rounded-lg p-6 space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {"Locking your assessment finalizes the data and generates your official VSME-compliant sustainability report. Once locked:"}
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{"Your sustainability data becomes"} <strong>{"read-only"}</strong> {"and official"}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{"You can"} <strong>{"generate and share"}</strong> {"public report links"}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{"Your report is ready for"} <strong>{"PDF export"}</strong> {"and stakeholder distribution"}</span>
                  </li>
                </ul>
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{"Complete Your Assessment First"}</AlertTitle>
                <AlertDescription>
                  {"Go to the workflow page to complete all required sections, then lock your assessment to generate your official report."}
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <p className="text-sm font-medium">{"Ready to finalize?"}</p>
                  <p className="text-xs text-muted-foreground">{"Review and lock your assessment"}</p>
                </div>
                <Button
                  size="lg"
                  className="gap-2 shadow-lg shadow-primary/20"
                  style={{ backgroundColor: '#00444C' }}
                  onClick={() => setLocation(assessmentId ? `/workflow/${assessmentId}` : "/assessments")}
                >
                  {"Go to Workflow"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Other errors
    return (
      <div className="space-y-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{"Error Loading Report"}</AlertTitle>
          <AlertDescription>
            {error?.message || "Failed to load dashboard data"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { assessment, carbonPerformance, esgStatus, trustIndicators, hotspots, recommendations, shareLink } = dashboardData;

  // Generate share URL
  const shareUrl = shareLink
    ? `${window.location.origin}/public/report/${shareLink.token}`
    : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-black text-slate-900">{"Sustainability Report"}</h1>
          <p className="text-base text-slate-500">
            {assessment.organizationName} • {carbonPerformance.reportingPeriod}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="gap-2 rounded-3xl bg-slate-500/10 hover:bg-slate-500/20 text-sm font-bold text-slate-500"
            onClick={() => setShowShareModal(true)}
          >
            <Share2 className="h-4 w-4 text-primary" /> {"Share"}
          </Button>
          {hasAiBudget && (
            <Button
              className="gap-2 rounded-3xl bg-primary hover:bg-primary/90 text-sm font-bold"
              onClick={handleGenerateTextReport}
              disabled={generateTextReport.isPending}
              data-testid="button-generate-text-report"
            >
              {generateTextReport.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              {generateTextReport.isPending ? "Generating..." : "Generate Text Report"}
            </Button>
          )}
          <Button
            className="gap-2 rounded-3xl bg-primary hover:bg-primary/90 text-sm font-bold"
            onClick={handleGeneratePdf}
            disabled={isGeneratingPdf || !assessment.locked}
          >
            {isGeneratingPdf ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            {isGeneratingPdf ? "Generating..." : "Generate PDF"}
          </Button>
          <Button
            className="gap-2 rounded-3xl bg-primary hover:bg-primary/90 text-sm font-bold"
            onClick={handleGenerateMeasuresPdf}
            disabled={isGeneratingMeasuresPdf || !assessment.locked}
            data-testid="button-generate-measures-pdf"
          >
            {isGeneratingMeasuresPdf ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            {isGeneratingMeasuresPdf ? "Generating..." : "Generate Measures PDF"}
          </Button>
        </div>
      </div>

      {/* Status Alert */}
      {assessment.locked ? (
        <div className="p-4 bg-primary/10 rounded-2xl flex items-start gap-3">
          <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-primary">{"Report Ready for Generation"}</span>
            <span className="text-sm text-primary/70">{"Your baseline data is complete and locked. Generate your Sustainability Report for stakeholder distribution."}</span>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 rounded-2xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-amber-800">{"Assessment Not Locked - Action Required"}</span>
            <span className="text-sm text-amber-800/80">
              {"Your assessment is still in draft mode. To generate a finalized, shareable report, please complete all required sections and lock your assessment in the "}
              <button
                onClick={() => setLocation(assessmentId ? `/workflow/${assessmentId}` : "/assessments")}
                className="underline font-semibold hover:text-amber-900"
              >
                {"workflow page"}
              </button>
              {". Once locked, your report data will be preserved and ready for stakeholder distribution."}
            </span>
          </div>
        </div>
      )}

      {/* Report Visualization Component */}
      <ReportVisualization data={dashboardData} showStatusAlert={true} />

      {/* Administrative Controls */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">{"Administrative Controls"}</h2>
        </div>
        <p className="text-base text-slate-500 mb-4">{"Manage report versioning and public sharing"}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Submission Version */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-4">
            <h3 className="text-base font-bold text-slate-900">{"Submission Version"}</h3>
            <div className="flex flex-col gap-2">
              <div className="p-3 bg-slate-50 rounded-2xl flex justify-between items-center">
                <span className="text-sm text-slate-900">
                  {`${assessment.reportingYear} Baseline`}
                </span>
                <span className="text-xs text-slate-500">
                  {`Organization: ${assessment.organizationName}`}
                </span>
              </div>
              
              
            </div>
          </div>

          {/* Public Share Link */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-4">
            <h3 className="text-base font-bold text-slate-900">{"Public Share Link"}</h3>
            <div className="flex flex-col gap-3">
              {shareLink ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
                      <span className="text-sm text-slate-900 truncate block">{shareUrl}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-2xl"
                      onClick={() => shareUrl && handleCopyShareLink(shareUrl)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {`${shareLink.viewCount} views`}
                      </span>
                      <span>
                        {`Expires: ${
                          shareLink.expiresAt
                            ? new Date(shareLink.expiresAt).toLocaleDateString()
                            : "Never"
                        }`}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7"
                      onClick={handleRevokeShareLink}
                      disabled={revokeShareLink.isPending}
                    >
                      {revokeShareLink.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <X className="h-3 w-3 mr-1" />
                          {"Revoke"}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <LinkIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{"Share your finalized report with stakeholders"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{"Generate a secure link that gives read-only access to this report."}</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-white rounded-2xl px-4"
                    onClick={() => setShowShareModal(true)}
                    disabled={!assessment.locked}
                  >
                    {"Generate Share Link"}
                  </Button>
                  {!assessment.locked && (
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {"Lock assessment first"}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              {"Share Report"}
            </DialogTitle>
            <DialogDescription>
              {"Generate a public link to share this report with stakeholders"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {shareLink ? (
              <>
                <div className="p-4 bg-muted rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">{"Active Share Link"}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl || ""}
                      className="flex-1 px-3 py-2 text-sm bg-white border rounded-md"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => shareUrl && handleCopyShareLink(shareUrl)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{"Views"}</p>
                      <div className="flex items-center gap-1 font-medium">
                        <Eye className="h-3 w-3" />
                        {shareLink.viewCount}
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{"Expires"}</p>
                      <p className="font-medium">
                        {shareLink.expiresAt
                          ? new Date(shareLink.expiresAt).toLocaleDateString()
                          : "Never"}
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleRevokeShareLink}
                  disabled={revokeShareLink.isPending}
                >
                  {revokeShareLink.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <X className="h-4 w-4 mr-2" />
                  )}
                  {"Revoke Link"}
                </Button>
              </>
            ) : (
              <>
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {"No active share link. Generate one to allow external stakeholders to view this report without logging in."}
                  </p>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>
                      {"Assessment must be locked to generate a share link"}
                    </span>
                  </div>
                </div>

                <Button
                  className="w-full"
                  style={{ backgroundColor: '#00444C' }}
                  onClick={handleGenerateShareLink}
                  disabled={generateShareLink.isPending || !assessment.locked}
                >
                  {generateShareLink.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <LinkIcon className="h-4 w-4 mr-2" />
                  )}
                  {"Generate Share Link"}
                </Button>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareModal(false)}>
              {"Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Preview Modal (for PDF - currently disabled) */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border/20">
              <div>
                <h2 className="text-2xl font-heading font-bold">{"PDF Generation"}</h2>
                <p className="text-sm text-muted-foreground mt-1">{"Coming Soon"}</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
              <div className="flex flex-col items-center justify-center py-20">
                <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">{"PDF generation will be implemented soon"}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {"Use \"Generate Text Report\" for now to get a markdown version"}
                </p>
              </div>
            </div>

            <div className="border-t border-border/20 p-6 bg-white flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                {"Close"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
